import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Site } from '../../shared/models/site';
import { ControlPoint } from '../../shared/models/controlPoint';
import { PopupCloseMode, PopupOpenMode } from '../../features/control-points/models/popupMode';

import { MENU_OPTIONS } from '../../shared/constants/left-menu-option';
import { EventData } from '../../features/vibration-events/models/event-data';
import { VibrationsChart } from '../../shared/models/charts/VibrationsChart';

@Injectable({
  providedIn: 'root'
})
export class CurrentStateService {

  private currentSiteSubject!: BehaviorSubject<Site | null>;
  public currentSite$!: Observable<Site | null>;

  private currentControlPointSubject!: BehaviorSubject<ControlPoint | null>;
  public currentControlPoint$!: Observable<ControlPoint | null>;

  private newControlPointCreatedSubject!: BehaviorSubject<ControlPoint | null>;
  public newControlPointCreated$!: Observable<ControlPoint | null>;

  private newEventCreatedSubject!: BehaviorSubject<EventData | null>;
  public newEventCreated$!: Observable<EventData | null>;

  private showLoadingSubject!: BehaviorSubject<boolean | null>;
  public showLoading$!: Observable<boolean | null>;

  private reportGenerationProgressSubject!: BehaviorSubject<boolean>;
  public reportGenerationProgress$!: Observable<boolean>;

  // use it when popup close/open whatever the reason
  private openPopupSubject!: BehaviorSubject<PopupOpenMode | null>;
  public openPopup$!: Observable<PopupOpenMode | null>;
  private closePopupSubject!: BehaviorSubject<PopupCloseMode | null>;
  public closePopup$!: Observable<PopupCloseMode | null>;

  private slideMenuSubject!: BehaviorSubject<boolean>;
  public slideMenu$!: Observable<boolean>;


  private currentMenuSubject!: BehaviorSubject<string | null>;
  public currentMenu$!: Observable<string | null>;

  private currentEventsSubject!: BehaviorSubject<EventData[]>;
  public currentEvents$!: Observable<EventData[]>;

  // New subject for event visibility updates
  private eventVisibilityUpdateSubject!: BehaviorSubject<{eventId: number, isHidden: boolean} | null>;
  public eventVisibilityUpdate$!: Observable<{eventId: number, isHidden: boolean} | null>;

  private currentEventSubject: BehaviorSubject<EventData | null>;
  public currentEvent$: Observable<EventData | null>;


  constructor() {
    this.currentSiteSubject = new BehaviorSubject<Site | null>(null);
    this.currentSite$ = this.currentSiteSubject.asObservable();

    this.currentControlPointSubject = new BehaviorSubject<ControlPoint | null>(null);
    this.currentControlPoint$ = this.currentControlPointSubject.asObservable();

    this.newControlPointCreatedSubject = new BehaviorSubject<ControlPoint | null>(null);
    this.newControlPointCreated$ = this.newControlPointCreatedSubject.asObservable();

    this.newEventCreatedSubject = new BehaviorSubject<EventData | null>(null);
    this.newEventCreated$ = this.newEventCreatedSubject.asObservable();

    this.showLoadingSubject = new BehaviorSubject<boolean | null>(null);
    this.showLoading$ = this.showLoadingSubject.asObservable();

    this.reportGenerationProgressSubject = new BehaviorSubject<boolean>(false);
    this.reportGenerationProgress$ = this.reportGenerationProgressSubject.asObservable();

    this.openPopupSubject = new BehaviorSubject<PopupOpenMode | null>(null);
    this.openPopup$ = this.openPopupSubject.asObservable();

    this.closePopupSubject = new BehaviorSubject<PopupCloseMode | null>(null);
    this.closePopup$ = this.closePopupSubject.asObservable();

    this.slideMenuSubject = new BehaviorSubject<boolean>(false);
    this.slideMenu$ = this.slideMenuSubject.asObservable();

    this.currentMenuSubject = new BehaviorSubject<string | null>(null);
    this.currentMenu$ = this.currentMenuSubject.asObservable();

    this.currentEventsSubject = new BehaviorSubject<EventData[]>([]);
    this.currentEvents$ = this.currentEventsSubject.asObservable();

    this.eventVisibilityUpdateSubject = new BehaviorSubject<{eventId: number, isHidden: boolean} | null>(null);
    this.eventVisibilityUpdate$ = this.eventVisibilityUpdateSubject.asObservable();

    this.currentEventSubject = new BehaviorSubject<EventData | null>(null);
    this.currentEvent$ = this.currentEventSubject.asObservable();
  }

  setCurrentSite(site: Site) {
    this.currentSiteSubject.next(site);
  }

  getCurrentSite(): Site | null {
    return this.currentSiteSubject.value;
  }

  setCurrentControlPoint(controlPoint: ControlPoint | null) {
    this.currentControlPointSubject.next(controlPoint);
  }

  setCurrentEvent(event: EventData) {
    this.currentEventSubject.next(event);
  }

  getCurrentEvent() {
    return this.currentEventSubject.value;
  }


  getCurrentControlPoint(): ControlPoint | null {
    return this.currentControlPointSubject.value;
  }

  getCurrentControlPointPosition(): [number, number] | null {
    if (this.currentControlPointSubject.value?.x && this.currentControlPointSubject.value?.y) {
      return [this.currentControlPointSubject.value?.x, this.currentControlPointSubject.value?.y];
    }
    return null;
  }

  setNewControlPointCreated(controlPointCreated: ControlPoint | null) {
    this.newControlPointCreatedSubject.next(controlPointCreated);
  }

  setNewEventCreated(event: EventData | null) {
    this.newEventCreatedSubject.next(event);
  }

  setShowLoading(show: boolean) {
    this.showLoadingSubject.next(show);
  }

  setReportGenerationInProgress(inProgress: boolean) {
    this.reportGenerationProgressSubject.next(inProgress);
  }

  setOpenPopup(openMode: PopupOpenMode) {
    this.openPopupSubject.next(openMode);
  }

  setClosePopup(closeMode: PopupCloseMode) {
    this.closePopupSubject.next(closeMode);
  }

  setCurrentEvents(events: EventData[]): EventData[] {
    this.currentEventsSubject.next(events);
    return events;
  }

  addEvent(newEvent: EventData, isEdit: boolean) {
    const currentEvents = this.currentEventsSubject.value;
    if (isEdit) {
      const eventIndex = currentEvents.findIndex(event => event.id === newEvent.id);
      if (eventIndex !== -1) {
        currentEvents[eventIndex] = newEvent;
      }
    } else {
      currentEvents.push(newEvent);
    }
    this.currentEventsSubject.next(currentEvents);
  }



  getCurrentMenu(): string | null {

    return this.currentMenuSubject.value;

  }

  setCurrentMenu(menuItem: string | null) {

    this.currentMenuSubject.next(menuItem);

  }

  getCurrentEvents(): EventData[] {

    return this.currentEventsSubject.value;

  }

  // New method to update event visibility without reloading all events
  updateEventVisibility(eventId: number, isHidden: boolean) {
    // Update the event in the current events list
    const currentEvents = this.currentEventsSubject.value;
    const eventIndex = currentEvents.findIndex(event => event.id === eventId);
    if (eventIndex !== -1) {
      currentEvents[eventIndex].isHidden = isHidden;
      this.currentEventsSubject.next([...currentEvents]);
    }
    
    // Notify map component about the visibility change
    this.eventVisibilityUpdateSubject.next({eventId, isHidden});
  }

  toggleSlideMenu(forceClose: boolean | null = null, menuItem: string | null = null, forceOpen: boolean | null = null) {

    if (forceOpen !== null && forceOpen) {
      this.slideMenuSubject.next(forceOpen);
    } else {
      if (this.currentMenuSubject.value === null) {
        this.currentMenuSubject.next(menuItem);
      }

      if (forceClose !== null && forceClose) {
        this.slideMenuSubject.next(forceClose);
      }

      if (menuItem === this.currentMenuSubject.value) {
        this.slideMenuSubject.next(!this.slideMenuSubject.value);
      } else {
        this.slideMenuSubject.next(true);
      }
    }
    if(menuItem){
    this.currentMenuSubject.next(menuItem);
    }
  }
}
